import { useEffect } from 'react';

/**
 * Structured Data Component
 * 
 * Adds JSON-LD structured data for better search engine understanding.
 * Implements Organization and SoftwareApplication schemas.
 */

interface OrganizationSchema {
  '@context': string;
  '@type': string;
  name: string;
  url: string;
  logo: string;
  description: string;
  foundingDate?: string;
  founders?: Array<{ '@type': string; name: string }>;
  address?: {
    '@type': string;
    addressCountry: string;
  };
  sameAs?: string[];
  contactPoint?: {
    '@type': string;
    contactType: string;
    email?: string;
  };
}

interface SoftwareApplicationSchema {
  '@context': string;
  '@type': string;
  name: string;
  applicationCategory: string;
  operatingSystem: string;
  offers: {
    '@type': string;
    price: string;
    priceCurrency: string;
  };
  aggregateRating?: {
    '@type': string;
    ratingValue: string;
    ratingCount: string;
  };
  description: string;
  url: string;
  image: string;
  author: {
    '@type': string;
    name: string;
  };
  softwareVersion?: string;
  datePublished?: string;
  releaseNotes?: string;
}

interface BreadcrumbSchema {
  '@context': string;
  '@type': string;
  itemListElement: Array<{
    '@type': string;
    position: number;
    name: string;
    item: string;
  }>;
}

interface StructuredDataProps {
  type?: 'organization' | 'software' | 'breadcrumb' | 'all';
  customSchema?: Record<string, any>;
  breadcrumbItems?: Array<{ name: string; url: string }>;
}

export const StructuredData = ({ 
  type = 'all',
  customSchema,
  breadcrumbItems
}: StructuredDataProps) => {
  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];

    // Helper to create and append script
    const addSchema = (schema: Record<string, any>, id: string) => {
      // Remove existing script if present
      const existing = document.getElementById(id);
      if (existing) {
        existing.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    };

    // Organization Schema
    if (type === 'organization' || type === 'all') {
      const organizationSchema: OrganizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Scope Manager',
        url: window.location.origin,
        logo: `${window.location.origin}/icon-512.png`,
        description: 'AI-powered project management and document intelligence platform for steel fabrication companies.',
        foundingDate: '2024',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'US',
        },
        sameAs: [
          // Add social media links when available
          // 'https://www.linkedin.com/company/scopemanager',
          // 'https://twitter.com/scopemanager',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
        },
      };
      addSchema(organizationSchema, 'organization-schema');
    }

    // Software Application Schema
    if (type === 'software' || type === 'all') {
      const softwareSchema: SoftwareApplicationSchema = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Scope Manager',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web Browser, iOS, Android',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        description: 'AI-powered document management and project intelligence platform for steel fabrication. Features include document upload, AI-powered search, team collaboration, and real-time document processing.',
        url: window.location.origin,
        image: `${window.location.origin}/icon-512.png`,
        author: {
          '@type': 'Organization',
          name: 'Scope Manager',
        },
        softwareVersion: '1.0.0',
        datePublished: '2024-01-01',
      };
      addSchema(softwareSchema, 'software-schema');
    }

    // Breadcrumb Schema
    if ((type === 'breadcrumb' || type === 'all') && breadcrumbItems && breadcrumbItems.length > 0) {
      const breadcrumbSchema: BreadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url.startsWith('http') ? item.url : `${window.location.origin}${item.url}`,
        })),
      };
      addSchema(breadcrumbSchema, 'breadcrumb-schema');
    }

    // Custom Schema
    if (customSchema) {
      addSchema(customSchema, 'custom-schema');
    }

    // Cleanup function
    return () => {
      scripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [type, customSchema, breadcrumbItems]);

  return null;
};
